// components/MyPage/MyComments.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Pagination, Card,
  CardContent, Stack, useMediaQuery, CircularProgress,
} from '@mui/material';
import CommentIcon from '@mui/icons-material/ChatBubbleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import api from '../../api/axios';

const PAGE_SIZE = 10;

export default function MyComments() {
  const [comments, setComments] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    api.get('/api/comments/my-comments/')
      .then(res => setComments(res.data.comments || []))
      .catch(() => {
        alert('내 댓글을 불러오지 못했습니다.');
        setComments([]);
      });
  }, []);

  if (comments === null) return <CircularProgress />;
  if (comments.length === 0) return <Typography sx={{ p: 3 }}>작성한 댓글이 없습니다.</Typography>;

  const pageCount = Math.ceil(comments.length / PAGE_SIZE);
  const slice = comments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const titleLimit = isMobile ? 5 : 10;
  const commentLimit = isMobile ? 5 : 10;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', px: 1 }}>
      {isMobile ? (
        <Stack spacing={2}>
          {slice.map((c) => {
            const isDeleted = c.is_deleted;
            const fullTitle = c.post_title || '(제목 없음)';
            const fullComment = isDeleted ? '삭제된 댓글입니다.' : c.content || '';

            const shortTitle =
              fullTitle.length > titleLimit ? fullTitle.slice(0, titleLimit) + '…' : fullTitle;
            const shortComment =
              fullComment.length > commentLimit
                ? fullComment.slice(0, commentLimit) + '…'
                : fullComment;

            return (
              <Card
                key={c.id}
                variant="outlined"
                sx={{
                  opacity: isDeleted ? 0.6 : 1,
                  cursor: isDeleted ? 'default' : 'pointer',
                  backgroundColor: isDeleted ? '#f5f5f5' : 'white',
                  '&:hover': {
                    backgroundColor: !isDeleted ? '#f9f9f9' : undefined,
                  },
                }}
                onClick={() =>
                  !isDeleted && navigate(`/main/posts/${c.post_id}#comment-${c.id}`)
                }
              >
                <CardContent>
                  <Stack spacing={1}>
                    {/* 댓글 내용 */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CommentIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        title={fullComment}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          textAlign: 'left',
                        }}
                      >
                        {shortComment}
                      </Typography>
                    </Stack>

                    {/* 게시글 제목 */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ArticleIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        title={fullTitle}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          textAlign: 'left',
                        }}
                      >
                        {shortTitle}
                      </Typography>
                    </Stack>

                    {/* 작성일 */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'left', alignSelf: 'flex-start' }}
                    >
                      {dayjs(c.created_at).format('YY.MM.DD HH:mm')}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>댓글 내용</TableCell>
                  <TableCell align="left" sx={{ width: 200 }}>게시글</TableCell> {/* ← 변경 */}
                  <TableCell align="center" sx={{ width: 120 }}>작성일</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slice.map((c) => {
                  const fullTitle = c.post_title || '(제목 없음)';
                  const fullComment = c.is_deleted ? '삭제된 댓글입니다.' : c.content || '';

                  const shortTitle =
                    fullTitle.length > titleLimit
                      ? fullTitle.slice(0, titleLimit) + '…'
                      : fullTitle;

                  const shortComment =
                    fullComment.length > commentLimit
                      ? fullComment.slice(0, commentLimit) + '…'
                      : fullComment;

                  return (
                    <TableRow
                      key={c.id}
                      hover={!c.is_deleted}
                      sx={{ cursor: c.is_deleted ? 'default' : 'pointer' }}
                      onClick={() =>
                        !c.is_deleted && navigate(`/main/posts/${c.post_id}#comment-${c.id}`)
                      }
                    >
                      {/* 댓글 내용 */}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CommentIcon fontSize="small" />
                          <Typography
                            variant="body2"
                            title={fullComment}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              flex: 1,
                              color: c.is_deleted ? 'text.disabled' : 'inherit',
                              textAlign: 'left',
                            }}
                          >
                            {shortComment}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* 게시글 제목 - 왼쪽 정렬로 변경 */}
                      <TableCell align="left">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ArticleIcon fontSize="small" />
                          <Typography
                            variant="body2"
                            title={fullTitle}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textAlign: 'left',
                            }}
                          >
                            {shortTitle}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* 작성일 */}
                      <TableCell align="center">
                        <Typography variant="body2">
                          {dayjs(c.created_at).format('YY-MM-DD')}
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(c.created_at).format('HH:mm')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            shape="rounded"
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
